import React, { useState } from "react";
import moment from "moment";
import PropTypes from "prop-types";
import {
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ErrorBar,
  Cell,
  ReferenceLine,
  ResponsiveContainer,
} from "recharts";
import { formatNumber } from "@signolteam/shared/units-of-measure";
import DropDown from "@src/components/atoms/DropDown";
import {
  roundDownToNearestIncrement,
  roundUpToNearestIncrement,
} from "@src/constants/convertions";
import { useDCContext } from "@src/context";
import { useManagerContext } from "@src/context/managerContext";
import AppUtils from "@src/utils/appUtils";
import { getNumberFormatterForUnit } from "@src/components/utility/getNumberFormatted";
import CardContainer from "@src/components/cards/CardContainer";
import TitleWithTooltip from "@src/components/atoms/TitleWithTooltip";
import Loader from "@src/components/atoms/Loader";

const stopStandardErrorGoingBelowMin = (
  dataPointValue,
  dataPointStandardError,
  domainMin,
) => {
  return dataPointValue - dataPointStandardError < domainMin
    ? dataPointStandardError + (dataPointValue - dataPointStandardError)
    : dataPointStandardError;
};

const stopStandardErrorGoingAboveMax = (
  dataPointValue,
  dataPointStandardError,
  domainMax,
) => {
  return dataPointValue + dataPointStandardError > domainMax
    ? dataPointStandardError -
        (dataPointValue + dataPointStandardError - domainMax)
    : dataPointStandardError;
};

const CustomTooltip = ({ active, payload, year, successChart }) => {
  const { profile } = useManagerContext();
  const { getDynamicReplacement } = useDCContext();
  const { industry, preferredEventName, preferredEventNamePlural } =
    profile.customer;
  const unit = successChart ? "%" : "kg";
  const formatFunction = getNumberFormatterForUnit(unit, industry);

  if (active && payload && payload.length) {
    const dcPrefix = successChart
      ? "fuel_manager__behaviour__success_section__success_over_time_chart"
      : "fuel_manager__behaviour__fuel_saving_section__fuel_saving_over_time_chart";

    const details = payload[0].payload;
    const currentAvg = getDynamicReplacement(
      `${dcPrefix}_tooltip__current_avg`,
      {
        BEHAVIOUR_ACRONYM: details.bvrAcronym,
      },
    );
    const diffVsPreSignol = getDynamicReplacement(
      `${dcPrefix}_tooltip__diff_vs_pre_signol`,
      {
        BEHAVIOUR_ACRONYM: details.bvrAcronym,
      },
    );
    const noData = getDynamicReplacement(`${dcPrefix}_tooltip__no_data`);
    const opportunities = getDynamicReplacement(
      `${dcPrefix}_tooltip__opportunities`,
      {
        NUMBER: formatNumber(details.opportunities),
        EVENT_NAME_PLURAL: preferredEventNamePlural,
      },
    );
    const opportunity = getDynamicReplacement(
      `${dcPrefix}_tooltip__opportunity`,
      {
        NUMBER: formatNumber(details.opportunities),
        EVENT_NAME: preferredEventName,
      },
    );

    const measurementPrimary = formatFunction(
      details.successPercent,
      details.standardError95?.[0],
    );
    const measurementBaselineChange = formatFunction(
      details.baselineChange,
      details.baselineChangeStandardError95,
      {
        includePositiveSign: true,
      },
    );
    return (
      <div className="bg-white shadow p-2">
        <div className="p-1.5">
          <div className="font-bold text-neutral-500">{`${
            details.fullMonth ? details.fullMonth : details.name
          } ${year}`}</div>
          {!!details.opportunities && (
            <div className="text-sm text-neutral-500">
              {details.opportunities === 1 ? opportunity : opportunities}
            </div>
          )}
        </div>
        <div className="border-t border-neutral-500 p-2">
          {details.noData ? (
            <div>{noData}</div>
          ) : (
            <table>
              <tbody>
                <tr>
                  <td>
                    <span className="font-bold text-lg">
                      {measurementPrimary.formattedValue}
                    </span>
                    <span
                      style={{ color: details.colour }}
                    >{` ±${measurementPrimary.formattedUncertainty}`}</span>
                  </td>
                  <td className="pl-2">
                    <span className="text-sm text-neutral-500">
                      {currentAvg}
                    </span>
                  </td>
                </tr>
                <tr>
                  <td>
                    <span className="font-bold text-lg">
                      {measurementBaselineChange.formattedValue}
                    </span>
                    <span
                      style={{ color: details.colour }}
                    >{` ±${measurementBaselineChange.formattedUncertainty}`}</span>
                  </td>
                  <td className="pl-2">
                    <span className="text-sm text-neutral-500">
                      {diffVsPreSignol}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          )}
        </div>
      </div>
    );
  }
  return null;
};

/** Success Rate (or Fuel Savings) bar chart per month, with horizontal line for pre-Signol "baseline" average
 * previously known as BehaviourSuccessOrSavingsVsPreSignol */
const BehaviourSuccessOrSavingsOverTime = ({
  periods = [],
  baseline = { value: null },
  successChart = true,
}) => {
  const { selectedBehaviour, selectedBehaviourColour } = useManagerContext();
  const { getDynamicReplacement } = useDCContext();

  //periods: Add props "month" and "year" if missing
  periods = (periods || []).map((p) => ({
    ...p,
    month: p.month || moment.utc(p.timestamp).format("MMMM"),
    year: p.year || moment.utc(p.timestamp).year(),
  }));

  const dcPrefix = successChart
    ? "fuel_manager__behaviour__success_section__success_over_time_chart"
    : "fuel_manager__behaviour__fuel_saving_section__fuel_saving_over_time_chart";

  const unit = successChart ? "%" : "kg";
  const txtKeyBaseline = getDynamicReplacement(`${dcPrefix}_key_baseline`);
  const txtTooltipContent = getDynamicReplacement(
    "fuel_manager__tooltip__behaviour_performance_baseline",
    {
      BEHAVIOUR_ACRONYM: selectedBehaviour.behaviourAcronym,
    },
  );
  const txtChartTitle = getDynamicReplacement(`${dcPrefix}_title`, {
    BEHAVIOUR_ACRONYM: selectedBehaviour.behaviourAcronym,
  });

  const yearsWithData = [...new Set(periods.map((period) => period.year))];
  const [yearToDisplay, setYearToDisplay] = useState(
    Math.max(...yearsWithData),
  );
  if (!periods) {
    return <div></div>;
  }

  let highestValue = 0;
  let lowestValue = 0;

  periods.forEach((period) => {
    let maxValue = period.value.value + period.value.standardError95;
    let minValue = period.value.value - period.value.standardError95;
    if (maxValue > highestValue) {
      highestValue = maxValue;
    }
    if (minValue < lowestValue) {
      lowestValue = minValue;
    }
  });

  const domainMin = successChart
    ? 0
    : lowestValue < -100
      ? roundDownToNearestIncrement(lowestValue, 100)
      : roundDownToNearestIncrement(lowestValue, 10);
  const domainMax = successChart
    ? 100
    : roundUpToNearestIncrement(highestValue, 10);
  const colour = selectedBehaviourColour.codes["300"];
  const errorBarColour = selectedBehaviourColour.codes["500"];
  const baselineColour = "#04454E";

  const dropDownOptions = yearsWithData.map((year) => ({
    value: year,
    text: `${year}`,
  }));
  const selectedYearPeriods = periods.filter(
    (period) => period.year === yearToDisplay,
  );

  const BehaviourSuccessOverTimeData = [];
  if (selectedYearPeriods.length > 0) {
    for (let m = 0; m < 12; m++) {
      const month = moment.utc().month(m);
      const periodData = selectedYearPeriods.filter(
        (period) => period.month === month.format("MMMM"),
      )[0];

      const item = {
        name: month.format("MMM"),
        fullMonth: month.format("MMMM"),
        successPercent: periodData?.value?.value || 0,
        opportunities: periodData?.countEventsPerformable || 0,
        baselineChange: periodData?.valueChangeFromBaseline?.value || 0,
        baselineChangeStandardError95:
          periodData?.valueChangeFromBaseline?.standardError95 || 0,
        noData: !periodData,
        bvrAcronym: selectedBehaviour.behaviourAcronym,
        colour,
      };
      if (typeof periodData?.value?.standardError95 === "number") {
        item["standardError95"] = [
          //need to stop the standardErrors from being out of the domain bounds as this will break the y axis labels
          AppUtils.limitDecimals(
            successChart
              ? stopStandardErrorGoingBelowMin(
                  periodData?.value?.value || 0,
                  periodData?.value?.standardError95 || 0,
                  domainMin,
                ) || 0
              : periodData?.value?.standardError95 || 0,
            2,
          ),
          AppUtils.limitDecimals(
            successChart
              ? stopStandardErrorGoingAboveMax(
                  periodData?.value?.value || 0,
                  periodData?.value?.standardError95 || 0,
                  domainMax,
                ) || 0
              : periodData?.value?.standardError95 || 0,
            2,
          ),
        ];
      }
      BehaviourSuccessOverTimeData.push(item);
    }
  }

  const ticks = successChart
    ? null
    : lowestValue < 0
      ? [
          domainMin,
          Math.floor(domainMin / 2),
          0,
          Math.floor(domainMax / 2),
          domainMax,
        ]
      : null;

  return (
    <CardContainer addaptivePadding allowOverflow zIndex={40}>
      <TitleWithTooltip
        title={txtChartTitle}
        tooltipContent={txtTooltipContent}
        centered={false}
        tooltipRightPosition={0}
      />
      <div>
        <div className="mb-3 flex flex-col md:flex-row md:justify-between">
          {yearsWithData.length >= 2 && (
            <div className="flex items-center mb-3 md:mb-0">
              <DropDown
                value={yearToDisplay}
                onChange={(e) => setYearToDisplay(parseInt(e))}
                options={dropDownOptions}
              />
            </div>
          )}
          {baseline?.value && (
            <div className="flex items-center self-end md:self-auto">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="21"
                height="2"
                viewBox="0 0 21 2"
                fill="none"
              >
                <path
                  d="M0 1H21"
                  stroke="#04454E"
                  strokeWidth="2"
                  strokeDasharray="5 5"
                />
              </svg>
              <span className="text-sm ml-3">{txtKeyBaseline}</span>
            </div>
          )}
        </div>
        <ResponsiveContainer width="100%" height={250}>
          <BarChart
            data={BehaviourSuccessOverTimeData}
            barCategoryGap={2}
            barGap={1}
            margin={{
              top: 5,
              right: 5,
              bottom: 5,
              left: successChart ? -20 : 0,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" fill="#F4F4F4" />
            <XAxis
              dataKey="name"
              tick={{ fill: "black", fontSize: "0.875rem" }}
            />
            <YAxis
              type="number"
              domain={[domainMin, domainMax]}
              tickCount={6}
              unit={unit}
              tick={{ fill: "black", fontSize: "0.875rem" }}
              ticks={ticks}
            />
            <Tooltip
              allowEscapeViewBox={{ x: false, y: true }}
              content={
                <CustomTooltip
                  year={yearToDisplay}
                  successChart={successChart}
                />
              }
              wrapperStyle={{ outline: "none" }}
            />
            <Bar dataKey="successPercent" barSize={20} barCategoryGap={12}>
              {BehaviourSuccessOverTimeData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.colour} />
              ))}
              <ErrorBar
                dataKey={`standardError95-${Math.random()}`}
                width={4}
                strokeWidth={2}
                stroke={errorBarColour}
                direction="y"
              />
            </Bar>
            {lowestValue < 0 && <ReferenceLine y={0} stroke="#000000" />}
            {baseline?.value && (
              <ReferenceLine
                y={baseline.value}
                stroke={baselineColour}
                strokeDasharray="6 6"
                strokeWidth="2"
              />
            )}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </CardContainer>
  );
};

export const behaviourSuccessOrSavingsOverTimeControls = {
  periods: {
    propType: PropTypes.array.isRequired,
    description: "Data grouped by Month",
    controlType: "object",
  },
  baseline: {
    propType: PropTypes.object,
    description: "Baseline average (for horizontal dashed line)",
    controlType: "object",
  },
  successChart: {
    propType: PropTypes.bool,
    description: "is success chart or not",
    controlType: "boolean",
  },
};

BehaviourSuccessOrSavingsOverTime.propTypes = AppUtils.getPropTypeFromControls(
  behaviourSuccessOrSavingsOverTimeControls,
);

export default BehaviourSuccessOrSavingsOverTime;
